import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, FileText, Download, Play, Check, X, FolderOpen, AlertCircle, Youtube, Instagram, Upload, CheckCircle, Calendar, ExternalLink, Trash2, Send, Eye, Edit, Loader2, Mail, Flag, ShieldCheck, Maximize2, Layers, Square, FileCheck, Wrench, Award, EyeOff, Droplets, Tv, DoorClosed, Magnet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import FileDropZone from "@/components/FileDropZone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MascotHint = ({ img, alt, text, testid }) => {
  const ref = useRef(null);
  // Riduce il font finché il fumetto sta su una riga (min 9px)
  useLayoutEffect(() => {
    const fit = () => {
      const el = ref.current;
      if (!el) return;
      let size = window.innerWidth < 640 ? 16 : 18;
      el.style.fontSize = `${size}px`;
      while (el.scrollWidth > el.clientWidth && size > 9) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [text]);
  return (
    <div className="flex items-center gap-2 sm:gap-6 sm:justify-center -mb-4" data-testid={testid}>
      <img src={img} alt={alt} className="genio-float w-14 sm:w-32 shrink-0 h-auto select-none pointer-events-none" draggable="false" />
      <div className="genio-bubble relative min-w-0 flex-1 sm:flex-none bg-white border-2 border-[#1e40af] rounded-2xl px-3 py-2 sm:px-5 sm:py-3 shadow-md">
        <p ref={ref} className="font-bold text-[#1e40af] whitespace-nowrap overflow-hidden" data-testid={`${testid}-text`}>{text}</p>
      </div>
    </div>
  );
};

const CARATTERISTICHE_PVC = [
  { icon: Flag, label: "Prodotto Italiano" },
  { icon: ShieldCheck, label: "10 anni di Garanzia" },
  { icon: Maximize2, label: "Profili Sottili" },
  { icon: Layers, label: "Tripla Guarnizione" },
  { icon: Square, label: "Vetro Incollato" },
  { icon: FileCheck, label: "Pratica Enea" },
  { icon: Wrench, label: "Posa Garantita a Vita" },
];

const CARATTERISTICHE_PORTE = [
  { icon: Flag, label: "Prodotto Italiano" },
  { icon: ShieldCheck, label: "10 anni di garanzia" },
  { icon: EyeOff, label: "Posa senza viti a vista" },
  { icon: Droplets, label: "Telaio Idrofugo" },
  { icon: Tv, label: "Viste in TV" },
  { icon: DoorClosed, label: "Cerniere nascoste" },
  { icon: Magnet, label: "Serrature Magnetiche" },
];

const CaratteristicheCard = ({ items, categoria, testid }) => (
  <Card data-testid={`caratteristiche-${testid}-card`}>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Award className="w-5 h-5 text-[#1e40af]" />
        Cosa ci contraddistingue
        <span className="text-sm font-normal text-slate-500">· {categoria}</span>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {items.map(({ icon: Icon, label }, i) => (
          <div
            key={label}
            className="flex flex-col items-center text-center gap-2 p-4 rounded-xl bg-blue-50 border border-blue-100 transition-transform duration-200 hover:-translate-y-0.5"
            data-testid={`caratteristica-${testid}-${i}`}
          >
            <div className="w-12 h-12 rounded-full bg-[#1e40af] text-white flex items-center justify-center">
              <Icon className="w-6 h-6" />
            </div>
            <span className="text-sm font-semibold text-slate-800">{label}</span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export default function ClientOfferPage() {
  const { magicLink } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [responded, setResponded] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const [activeTab, setActiveTab] = useState("offerta"); // Inizia sempre con "offerta"
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [signedFilesByTipo, setSignedFilesByTipo] = useState({});
  // Documenti personali
  const [docPersFiles, setDocPersFiles] = useState({
    codice_fiscale_fronte: null,
    codice_fiscale_retro: null,
    documento_identita_fronte: null,
    documento_identita_retro: null,
  });
  const [uploadingDocPers, setUploadingDocPers] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploadingRevisione, setUploadingRevisione] = useState(false);
  const [revisionePreviewOpen, setRevisionePreviewOpen] = useState(false);
  const [revisionePreview, setRevisionePreview] = useState({
    loading: false, sending: false, editMode: false,
    subject: "", html: "", text: "", to: "", bcc: "",
  });

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const response = await axios.get(`${API}/client/offer/${magicLink}`);
        setData(response.data);
        
        // Se il querystring richiede la tab documenti-personali → apri quella
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get("tab");
        if (tabFromUrl === "documenti-personali" && response.data.offer.documenti_personali_richiesti) {
          setActiveTab("documenti-personali");
        } else if (response.data.offer.documenti && response.data.offer.documenti.length > 0) {
          // Se ci sono documenti da firmare, mostra quella tab di default
          setActiveTab("documenti");
        }
        
        if (response.data.offer.stato !== "In Attesa Cliente") {
          setResponded(true);
        }
      } catch (e) {
        console.error("Error fetching offer:", e);
        setError("Offerta non trovata o link non valido");
      } finally {
        setLoading(false);
      }
    };
    fetchOffer();
  }, [magicLink]);

  // Rileva se l'utente ha una sessione CRM valida (per mostrare il Box Revisione anche senza PDF)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/client/offer/${magicLink}/is-admin`, { withCredentials: true });
        if (!cancelled) setIsAdmin(!!res.data?.is_admin);
      } catch (_) { /* utente non autenticato: resta false */ }
    })();
    return () => { cancelled = true; };
  }, [magicLink]);

  const refreshOffer = async () => {
    try {
      const r = await axios.get(`${API}/client/offer/${magicLink}`);
      setData(r.data);
    } catch (_) { /* noop */ }
  };

  const handleUploadRevisione = async (file) => {
    if (!file) return;
    setUploadingRevisione(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        `${API}/offers/${data.offer.id}/upload-revisione`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success('Offerta revisionata caricata. Clicca "Notifica Cliente" per inviarla.');
      await refreshOffer();
    } catch (e) {
      console.error("Error upload revisione:", e);
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploadingRevisione(false);
    }
  };

  const handleDeleteRevisione = async () => {
    if (!window.confirm("Eliminare l'offerta revisionata caricata?")) return;
    try {
      await axios.delete(`${API}/offers/${data.offer.id}/revisione`, { withCredentials: true });
      toast.success("Offerta revisionata eliminata");
      await refreshOffer();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const openRevisionePreview = async () => {
    setRevisionePreviewOpen(true);
    setRevisionePreview((s) => ({ ...s, loading: true, editMode: false }));
    try {
      const res = await axios.post(
        `${API}/offers/${data.offer.id}/notifica-revisione`,
        { dry_run: true },
        { withCredentials: true }
      );
      setRevisionePreview((s) => ({
        ...s, loading: false,
        subject: res.data.subject || "",
        html: res.data.html || "",
        text: res.data.text || "",
        to: res.data.to || "",
        bcc: res.data.bcc || "info@showroomingenio.it",
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento anteprima");
      setRevisionePreviewOpen(false);
      setRevisionePreview((s) => ({ ...s, loading: false }));
    }
  };

  const confirmSendRevisione = async () => {
    setRevisionePreview((s) => ({ ...s, sending: true }));
    try {
      await axios.post(
        `${API}/offers/${data.offer.id}/notifica-revisione`,
        { subject: revisionePreview.subject, html: revisionePreview.html, text: revisionePreview.text },
        { withCredentials: true }
      );
      toast.success("Email di notifica revisione inviata al cliente!");
      setRevisionePreviewOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'invio");
    } finally {
      setRevisionePreview((s) => ({ ...s, sending: false }));
    }
  };

  const handleUploadDocPersonali = async () => {
    const anyFile = Object.values(docPersFiles).some(f => f !== null);
    if (!anyFile) {
      toast.error("Seleziona almeno un file da caricare");
      return;
    }
    // Validazione locale: max 2MB e formato PDF/JPEG
    const MAX = 2 * 1024 * 1024;
    const ALLOWED = ["application/pdf", "image/jpeg", "image/jpg"];
    const errorHint = "Invia il file via mail a amministrazione@showroomingenio.it";
    for (const f of Object.values(docPersFiles)) {
      if (!f) continue;
      if (!ALLOWED.includes(f.type)) {
        toast.error(`"${f.name}" non è ammesso: solo PDF o JPEG. ${errorHint}`, { duration: 8000 });
        return;
      }
      if (f.size > MAX) {
        const mb = (f.size / 1024 / 1024).toFixed(2);
        toast.error(`"${f.name}" supera 2 MB (attuali: ${mb} MB). ${errorHint}`, { duration: 8000 });
        return;
      }
    }
    setUploadingDocPers(true);
    const formData = new FormData();
    for (const [k, f] of Object.entries(docPersFiles)) {
      if (f) formData.append(k, f);
    }
    try {
      const res = await axios.post(
        `${API}/client/offer/${magicLink}/upload-documenti-personali`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${res.data.caricati} documento/i caricato/i correttamente`);
      // Refresh offer data
      const refreshed = await axios.get(`${API}/client/offer/${magicLink}`);
      setData(refreshed.data);
      setDocPersFiles({
        codice_fiscale_fronte: null,
        codice_fiscale_retro: null,
        documento_identita_fronte: null,
        documento_identita_retro: null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento documenti", { duration: 8000 });
    } finally {
      setUploadingDocPers(false);
    }
  };

  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSfRgxSVycdCM2YZg6V9Xg-fNOYtXM1L8VtoobiWUfmDnKq7Bw/viewform?usp=dialog";

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      // Redirect al Google Form
      window.open(GOOGLE_FORM_URL, '_blank');
      setShowCountdown(false);
      setCountdown(null);
    }
  }, [countdown]);

  const handleResponse = async (accepted) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/offer/${magicLink}/respond`, {
        accepted,
        note: note.trim() || ""
      });
      
      if (accepted) {
        setResponded(true);
        setShowCountdown(true);
        setCountdown(5);
        toast.success("Grazie, la tua scelta è stata registrata");
      } else {
        toast.success("Risposta inviata");
        setResponded(true);
      }
      
      // Refresh data
      const response = await axios.get(`${API}/client/offer/${magicLink}`);
      setData(response.data);
    } catch (e) {
      console.error("Error responding:", e);
      toast.error("Errore nell'invio della risposta");
    } finally {
      setSubmitting(false);
    }
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    const trimmed = url.trim();
    // YouTube (youtube.com/watch, youtu.be, youtube.com/embed, youtube.com/shorts)
    const ytMatch = trimmed.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Tella: https://www.tella.tv/video/<id>/view  → /embed
    const tellaMatch = trimmed.match(/tella\.tv\/video\/([^/?#]+)/i);
    if (tellaMatch) return `https://www.tella.tv/video/${tellaMatch[1]}/embed?b=0&title=0&a=1&loop=0&t=0&muted=0&wt=0`;
    // Loom: https://www.loom.com/share/<id>  → /embed/<id>
    const loomMatch = trimmed.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/i);
    if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
    // Vimeo: https://vimeo.com/<id> → https://player.vimeo.com/video/<id>
    const vimeoMatch = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    // Fallback: se è già un URL di embed, usalo così com'è
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return null;
  };
  // Backward-compat alias (in caso di altri riferimenti nel codice)
  const getYoutubeEmbedUrl = getVideoEmbedUrl;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    
    // Valida ogni file
    const invalidFiles = [];
    const validFiles = [];
    
    files.forEach(file => {
      // Verifica estensione PDF
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        invalidFiles.push(`${file.name}: solo file PDF sono ammessi`);
        return;
      }
      // Verifica dimensione
      if (file.size > maxSize) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        invalidFiles.push(`${file.name}: dimensione ${sizeMB}MB supera il limite di 5MB`);
        return;
      }
      validFiles.push(file);
    });
    
    if (invalidFiles.length > 0) {
      toast.error(
        <div>
          <p className="font-semibold mb-1">File non validi:</p>
          {invalidFiles.map((msg, i) => (
            <p key={`invalid-${msg}-${i}`} className="text-sm">• {msg}</p>
          ))}
          <p className="text-sm mt-2">Carica solo file PDF inferiori a 5MB.</p>
        </div>,
        { duration: 6000 }
      );
    }
    
    setSelectedFiles(validFiles);
  };

  const handleSignedSlotSelect = (slotKey, file) => {
    if (!file) {
      setSignedFilesByTipo((prev) => {
        const next = { ...prev };
        delete next[slotKey];
        return next;
      });
      return;
    }
    const maxSize = 5 * 1024 * 1024;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(`${file.name}: solo file PDF sono ammessi`);
      return;
    }
    if (file.size > maxSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      toast.error(`${file.name}: dimensione ${sizeMB}MB supera il limite di 5MB`);
      return;
    }
    setSignedFilesByTipo((prev) => ({ ...prev, [slotKey]: file }));
  };

  const handleUploadDocumentiFirmati = async () => {
    const entries = Object.entries(signedFilesByTipo).filter(([, f]) => f);
    if (entries.length === 0) {
      toast.error("Seleziona almeno un file");
      return;
    }

    setUploadingDocs(true);
    try {
      const formData = new FormData();
      entries.forEach(([tipo, file]) => {
        formData.append('files', file);
        formData.append('tipi', tipo);
      });

      await axios.post(
        `${API}/client/offer/${magicLink}/upload-documenti-firmati`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success("Documenti caricati con successo! Riceverai una conferma via email.");
      setSignedFilesByTipo({});

      // Ricarica i dati per mostrare i documenti caricati
      const response = await axios.get(`${API}/client/offer/${magicLink}`);
      setData(response.data);

    } catch (error) {
      console.error(error);
      toast.error("Errore nel caricamento dei documenti");
    } finally {
      setUploadingDocs(false);
    }
  };

  // Upload singolo documento firmato (per card per-doc)
  const handleUploadSingleFirmato = async (tipo) => {
    const file = signedFilesByTipo[tipo];
    if (!file) {
      toast.error("Seleziona prima un file PDF");
      return;
    }
    setUploadingDocs(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('tipi', tipo);

      await axios.post(
        `${API}/client/offer/${magicLink}/upload-documenti-firmati`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success("Documento caricato con successo");
      setSignedFilesByTipo((prev) => {
        const next = { ...prev };
        delete next[tipo];
        return next;
      });
      const response = await axios.get(`${API}/client/offer/${magicLink}`);
      setData(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Errore nel caricamento del documento");
    } finally {
      setUploadingDocs(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Errore</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { offer, marketing_docs, youtube_link } = data;
  
  // Usa video personalizzato se presente, altrimenti quello comune
  const videoToShow = offer.video_personalizzato || youtube_link;
  const embedUrl = getYoutubeEmbedUrl(videoToShow);
  
  // Usa doc marketing personalizzato se presente, altrimenti quelli comuni
  const hasPersonalizedDoc = offer.doc_marketing_personalizzato;
  const cataloghiDocs = hasPersonalizedDoc ? [] : (marketing_docs?.filter((d) => d.categoria === "Cataloghi") || []);
  const schedeDocs = hasPersonalizedDoc ? [] : (marketing_docs?.filter((d) => d.categoria === "Schede") || []);

  return (
    <div className="client-portal" data-testid="client-offer-page">
      {/* Hero */}
      <div className="client-hero">
        <div className="max-w-4xl mx-auto">
          {/* Logo e info azienda */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center shadow-lg overflow-hidden p-1">
              <img src="/logo-ingenio.jpg" alt="Showroom Ingenio" className="w-full h-full object-contain" data-testid="portal-logo" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white drop-shadow-md">Showroom Ingenio</h1>
              <p className="text-sky-200 text-sm font-medium">
                Sede di Cagliari Via Puccini 71 - <a href="https://www.showroomingenio.it" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">www.showroomingenio.it</a>
              </p>
            </div>
          </div>
          
          {/* Saluto cliente */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <p className="text-sky-200 text-lg mb-2 font-medium">Benvenuto nel tuo Portale Personale</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-3 drop-shadow-lg">
              {offer.nome} {offer.cognome}
            </h2>
            <p className="text-xl text-sky-100 font-light">
              Ecco la tua offerta personalizzata
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Tab Navigation - Sempre visibile */}
        <div className="mb-8">
          <div className="border-b border-slate-200">
            <nav className="flex gap-8" aria-label="Tabs">
              {offer.stato === "accettato" && offer.documenti && offer.documenti.length > 0 && (
                <button
                  onClick={() => setActiveTab("documenti")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors bg-transparent ${
                    activeTab === "documenti"
                      ? "border-amber-500 text-amber-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  📋 Contrattualistica da Firmare
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 font-semibold">
                    URGENTE
                  </span>
                </button>
              )}
              {offer.documenti_personali_richiesti && (offer.documenti_personali_richiesti.codice_fiscale || offer.documenti_personali_richiesti.documento_identita) && (
                <button
                  onClick={() => setActiveTab("documenti-personali")}
                  data-testid="tab-documenti-personali"
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors bg-transparent ${
                    activeTab === "documenti-personali"
                      ? "border-purple-500 text-purple-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  🪪 Documenti Personali
                  {(!offer.documenti_personali_caricati || offer.documenti_personali_caricati.length === 0) && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 font-semibold">
                      RICHIESTO
                    </span>
                  )}
                </button>
              )}
              {offer.stato === "accettato" && ((offer.fatture && offer.fatture.length > 0) || (offer.collaudo && offer.collaudo.length > 0)) && (
                <button
                  onClick={() => setActiveTab("fatture-collaudo")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors bg-transparent ${
                    activeTab === "fatture-collaudo"
                      ? "border-emerald-500 text-emerald-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  📑 Fatture e Collaudo
                </button>
              )}
              <button
                onClick={() => setActiveTab("offerta")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors bg-transparent ${
                  activeTab === "offerta"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                }`}
              >
                📄 Offerta e Materiali
              </button>
              <button
                onClick={() => setActiveTab("contratto-fatture")}
                data-testid="tab-contratto-fatture"
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors bg-transparent ${
                  activeTab === "contratto-fatture"
                    ? "border-teal-500 text-teal-600"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                }`}
              >
                📁 Contratto e Fatture
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content: Contrattualistica */}
        {offer.stato === "accettato" && offer.documenti && offer.documenti.length > 0 && activeTab === "documenti" && (
          <div className="mb-8">
            <Card className="border-2 border-amber-300 bg-amber-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-900">
                  <FileText className="w-5 h-5" />
                  📋 Contrattualistica da Firmare
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Avviso Docusign se ci sono documenti flaggati */}
                {(offer.docusign_flags?.condizioni_contrattuali || offer.docusign_flags?.norme_uni) && (
                  <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
                    <div className="flex gap-3">
                      <AlertCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-purple-900 mb-2">📧 Firma Elettronica tramite Docusign</p>
                        <p className="text-sm text-purple-800 mb-2">
                          I seguenti documenti sono stati inviati per la firma elettronica:
                        </p>
                        <ul className="text-sm text-purple-800 list-disc list-inside mb-3">
                          {offer.docusign_flags?.condizioni_contrattuali && (
                            <li><strong>Condizioni Contrattuali</strong></li>
                          )}
                          {offer.docusign_flags?.norme_uni && (
                            <li><strong>Norme UNI</strong></li>
                          )}
                        </ul>
                        <p className="text-sm text-purple-900 font-bold">
                          ✉️ Verificare la propria mail per seguire il processo di firma da Smartphone o Tablet
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Alert */}
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-900 mb-2">⚠️ ATTENZIONE</p>
                      <p className="text-sm text-red-800">
                        Firmare i documenti e inviarli via mail a{" "}
                        <a 
                          href="mailto:amministrazione@showroomingenio.it" 
                          className="font-bold underline hover:text-red-900"
                        >
                          amministrazione@showroomingenio.it
                        </a>
                      </p>
                      <p className="text-sm text-red-800 font-bold mt-2">
                        Senza le firme l'Ordine non viene evaso.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Documenti - una sezione unificata per ogni documento richiesto */}
                <div className="space-y-4">
                  {offer.documenti.map((doc, index) => {
                    const nomiDocumenti = {
                      post_esecutivo: "Post Esecutivo",
                      post_esecutivo_2: "Post Esecutivo 2",
                      post_esecutivo_3: "Post Esecutivo 3",
                      condizioni_contrattuali: "Condizioni Contrattuali",
                      norme_uni: "Norme UNI",
                      condizioni_vendita_2026: "Condizioni di Vendita – Set Contrattuale 2026",
                      norma_uni_2026: "Norma UNI – Set Contrattuale 2026",
                    };
                    const nomeVisualizzato = doc.label || nomiDocumenti[doc.tipo] || doc.tipo;
                    const key = doc.tipo || `slot-${index}`;
                    const firmatoCaricato = (offer.documenti_firmati || []).find(
                      (f) => f.tipo && doc.tipo && f.tipo === doc.tipo
                    );
                    const currentFile = signedFilesByTipo[key] || null;
                    const ricevutoDaAdmin = !!doc.ricevuto_firmato;

                    return (
                      <div
                        key={doc.tipo || doc.file_path || `doc-${index}`}
                        className={`bg-white border-2 rounded-lg p-4 ${
                          ricevutoDaAdmin
                            ? "border-emerald-300 bg-emerald-50"
                            : firmatoCaricato
                              ? "border-green-300"
                              : "border-slate-200"
                        }`}
                        data-testid={`doc-card-${key}`}
                      >
                        {/* Header: nome + badge stato + scarica */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <FileText className={`w-5 h-5 shrink-0 mt-0.5 ${ricevutoDaAdmin ? "text-emerald-600" : "text-blue-600"}`} />
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-900">
                                {index + 1}. {nomeVisualizzato}
                              </p>
                              {ricevutoDaAdmin && (
                                <p className="text-xs text-emerald-700 font-medium mt-1 flex items-center gap-1">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Ricevuto dall'amministrazione
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <a
                              href={`${API}/files/${doc.file_path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button size="sm" variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-50">
                                <Eye className="w-4 h-4 mr-2" />
                                Visualizza
                              </Button>
                            </a>
                            <a
                              href={`${API}/files/${doc.file_path}?download=true`}
                              download
                            >
                              <Button size="sm" className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white">
                                <Download className="w-4 h-4 mr-2" />
                                Scarica
                              </Button>
                            </a>
                          </div>
                        </div>

                        {/* Zona upload firmato - nascosta se già ricevuto dall'admin */}
                        {!ricevutoDaAdmin && (
                          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                            <Label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wide">
                              Carica il PDF firmato
                            </Label>
                            <FileDropZone
                              accept=".pdf,application/pdf"
                              dropLabel={`Rilascia "${nomeVisualizzato}" firmato`}
                              onFile={(f) => handleSignedSlotSelect(key, f)}
                              className="block"
                            >
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => handleSignedSlotSelect(key, e.target.files?.[0] || null)}
                                className="block w-full text-sm text-slate-500
                                  file:mr-3 file:py-1.5 file:px-3
                                  file:rounded-full file:border-0
                                  file:text-sm file:font-semibold
                                  file:bg-green-100 file:text-green-700
                                  hover:file:bg-green-200
                                  cursor-pointer"
                                data-testid={`signed-slot-${key}`}
                              />
                            </FileDropZone>
                            {currentFile && (
                              <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                                <p className="text-xs text-green-700 flex items-center gap-1">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  <span className="font-medium">{currentFile.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleSignedSlotSelect(key, null)}
                                    className="ml-2 text-red-600 hover:text-red-800 underline text-xs"
                                    data-testid={`signed-slot-remove-${key}`}
                                  >
                                    rimuovi
                                  </button>
                                </p>
                                <Button
                                  size="sm"
                                  onClick={() => handleUploadSingleFirmato(key)}
                                  disabled={uploadingDocs}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                                  data-testid={`upload-single-${key}`}
                                >
                                  <Upload className="w-3.5 h-3.5 mr-1" />
                                  {uploadingDocs ? "Invio..." : "Invia firmato"}
                                </Button>
                              </div>
                            )}

                            {/* Registro caricamenti con data e ora */}
                            {firmatoCaricato && (
                              <div className="mt-3 pt-3 border-t border-slate-200">
                                <p className="text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">
                                  Registro caricamento
                                </p>
                                <div className="flex items-center gap-2 text-xs text-slate-700 flex-wrap">
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600 shrink-0" />
                                  <span>
                                    Caricato il{" "}
                                    <strong>
                                      {new Date(firmatoCaricato.data_caricamento).toLocaleString('it-IT', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </strong>
                                  </span>
                                  <a
                                    href={`${API}/files/${firmatoCaricato.file_path}`}
                                    download={firmatoCaricato.original_filename || firmatoCaricato.file_path}
                                    className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                                  >
                                    <Download className="w-3 h-3" />
                                    {firmatoCaricato.original_filename || "scarica"}
                                  </a>
                                  <span className="text-amber-700 italic">
                                    (puoi caricare un nuovo PDF per sostituirlo)
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-slate-600 italic">
                  ℹ️ Dopo ogni caricamento, l'amministrazione riceve una notifica automatica. Puoi caricare anche solo alcuni documenti ora e completare in seguito.
                </p>
              </CardContent>
            </Card>
          </div>
        )}


        {/* Tab Content: Documenti Personali */}
        {activeTab === "documenti-personali" && offer.documenti_personali_richiesti && (
          <div className="mb-8" data-testid="documenti-personali-content">
            <Card className="border-2 border-purple-300 bg-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <FileText className="w-5 h-5" />
                  🪪 Documenti Personali Richiesti
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4" data-testid="doc-personali-info">
                  <p className="text-sm text-amber-900 font-semibold mb-1">
                    ⚠️ Formati e dimensioni ammessi
                  </p>
                  <p className="text-sm text-amber-900">
                    Sono accettati esclusivamente file in formato <strong>PDF o JPEG</strong>, con dimensione <strong>massima di 2 MB</strong> per ogni file.
                  </p>
                  <p className="text-sm text-amber-900 mt-2">
                    Se un file supera il limite o non è nel formato giusto, riceverai un errore. In quel caso invialo via mail a{" "}
                    <a href="mailto:amministrazione@showroomingenio.it" className="font-bold underline">
                      amministrazione@showroomingenio.it
                    </a>.
                  </p>
                </div>

                {/* Codice Fiscale */}
                {offer.documenti_personali_richiesti.codice_fiscale && (
                  <div className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="section-cf">
                    <h3 className="font-semibold text-slate-800 mb-3">Codice Fiscale</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Fronte</label>
                        <FileDropZone
                          accept="application/pdf,image/jpeg,image/jpg,.pdf,.jpg,.jpeg"
                          dropLabel="Rilascia il file"
                          onFile={(f) => setDocPersFiles(p => ({ ...p, codice_fiscale_fronte: f }))}
                          className="block"
                        >
                          <input
                            type="file"
                            accept="application/pdf,image/jpeg,image/jpg"
                            onChange={(e) => setDocPersFiles(p => ({ ...p, codice_fiscale_fronte: e.target.files?.[0] || null }))}
                            className="block w-full text-sm border border-slate-300 rounded-md file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 cursor-pointer"
                            data-testid="input-cf-fronte"
                          />
                        </FileDropZone>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Retro</label>
                        <FileDropZone
                          accept="application/pdf,image/jpeg,image/jpg,.pdf,.jpg,.jpeg"
                          dropLabel="Rilascia il file"
                          onFile={(f) => setDocPersFiles(p => ({ ...p, codice_fiscale_retro: f }))}
                          className="block"
                        >
                          <input
                            type="file"
                            accept="application/pdf,image/jpeg,image/jpg"
                            onChange={(e) => setDocPersFiles(p => ({ ...p, codice_fiscale_retro: e.target.files?.[0] || null }))}
                            className="block w-full text-sm border border-slate-300 rounded-md file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 cursor-pointer"
                            data-testid="input-cf-retro"
                          />
                        </FileDropZone>
                      </div>
                    </div>
                  </div>
                )}

                {/* Documento Identità */}
                {offer.documenti_personali_richiesti.documento_identita && (
                  <div className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="section-docid">
                    <h3 className="font-semibold text-slate-800 mb-3">Documento di Identità</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Fronte</label>
                        <FileDropZone
                          accept="application/pdf,image/jpeg,image/jpg,.pdf,.jpg,.jpeg"
                          dropLabel="Rilascia il file"
                          onFile={(f) => setDocPersFiles(p => ({ ...p, documento_identita_fronte: f }))}
                          className="block"
                        >
                          <input
                            type="file"
                            accept="application/pdf,image/jpeg,image/jpg"
                            onChange={(e) => setDocPersFiles(p => ({ ...p, documento_identita_fronte: e.target.files?.[0] || null }))}
                            className="block w-full text-sm border border-slate-300 rounded-md file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 cursor-pointer"
                            data-testid="input-docid-fronte"
                          />
                        </FileDropZone>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Retro</label>
                        <FileDropZone
                          accept="application/pdf,image/jpeg,image/jpg,.pdf,.jpg,.jpeg"
                          dropLabel="Rilascia il file"
                          onFile={(f) => setDocPersFiles(p => ({ ...p, documento_identita_retro: f }))}
                          className="block"
                        >
                          <input
                            type="file"
                            accept="application/pdf,image/jpeg,image/jpg"
                            onChange={(e) => setDocPersFiles(p => ({ ...p, documento_identita_retro: e.target.files?.[0] || null }))}
                            className="block w-full text-sm border border-slate-300 rounded-md file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 cursor-pointer"
                            data-testid="input-docid-retro"
                          />
                        </FileDropZone>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleUploadDocPersonali}
                  disabled={uploadingDocPers}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white text-lg py-6"
                  data-testid="btn-upload-doc-personali"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  {uploadingDocPers ? "Caricamento in corso..." : "Carica Documenti"}
                </Button>

                {/* Riepilogo documenti caricati */}
                {(offer.documenti_personali_caricati || []).length > 0 && (
                  <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4" data-testid="documenti-personali-caricati">
                    <p className="text-lg font-bold text-emerald-800 mb-3 flex items-center gap-2">
                      ✅ Documenti Caricati Correttamente
                    </p>
                    <ul className="space-y-2">
                      {(offer.documenti_personali_caricati || []).map((doc, idx) => (
                        <li key={doc.slot || `doc-pers-${idx}`} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white rounded-lg p-2 border border-emerald-200">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span className="font-medium text-slate-800 shrink-0">{doc.label}</span>
                            <span className="text-slate-500 italic truncate text-sm">{doc.filename}</span>
                          </div>
                          <a
                            href={`${API}/client/offer/${magicLink}/documenti-personali/${doc.slot}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`download-doc-personale-${doc.slot}`}
                            className="shrink-0"
                          >
                            <Button size="sm" variant="outline" className="text-xs border-emerald-400 text-emerald-700 hover:bg-emerald-100">
                              <Download className="w-3 h-3 mr-1" />
                              Scarica
                            </Button>
                          </a>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 text-xs text-emerald-700">
                      Puoi ricaricare i documenti se necessario: i nuovi file sostituiranno i precedenti.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}


        {/* Tab Content: Offerta (contenuto esistente) */}
        {activeTab === "offerta" && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Main content */}
          <div className="md:col-span-8 space-y-8">
            {/* Genio fluttuante - invito a guardare il video */}
            {embedUrl && <MascotHint img="/genio-psss.png" alt="Genio Showroom Ingenio" text="Psss: Guarda il video prima di scegliere!" testid="genio-video-hint" />}

            {/* Video - Prima posizione */}
            {embedUrl && (
              <Card data-testid="video-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5 text-rose-500" />
                    Video Presentazione
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video rounded-lg overflow-hidden bg-slate-900">
                    <iframe
                      src={embedUrl}
                      title="Video Presentazione"
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cosa ci contraddistingue - visibile solo se flag attivo */}
            {offer.riepilogo_caratteristiche_pvc && <CaratteristicheCard items={CARATTERISTICHE_PVC} categoria="Infissi in PVC" testid="pvc" />}
            {offer.riepilogo_caratteristiche_porte && <CaratteristicheCard items={CARATTERISTICHE_PORTE} categoria="Porte Interne" testid="porte" />}

            {/* Video Aggiuntivo - Subito dopo video principale */}
            {offer.video_aggiuntivo && getVideoEmbedUrl(offer.video_aggiuntivo) && (
              <Card data-testid="video-aggiuntivo-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5 text-blue-500" />
                    Guarda anche questo video!
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-slate-100">
                    <iframe
                      width="100%"
                      height="100%"
                      src={getVideoEmbedUrl(offer.video_aggiuntivo)}
                      title="Video Aggiuntivo"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Offer PDF - Seconda posizione. Mostriamo tutti gli slot caricati (1-4). */}
            {(() => {
              const slots = [1, 2, 3, 4]
                .map((s) => ({
                  slot: s,
                  file: s === 1 ? offer.offerta_pdf : offer[`offerta_pdf_${s}`],
                  titolo: (s === 1 ? offer.offerta_pdf_titolo : offer[`offerta_pdf_${s}_titolo`]) || "",
                }))
                .filter((x) => x.file);
              if (slots.length === 0) {
                return (
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                      <p className="text-slate-600">L&apos;offerta è in fase di preparazione. Torna presto!</p>
                    </CardContent>
                  </Card>
                );
              }
              return (
                <>
                <MascotHint img="/volpe-furba.png" alt="Volpe Showroom Ingenio" text="Ehi! Fatti furbo! Non scegliere in base al prezzo!" testid="volpe-offerta-hint" />
                <div className="space-y-4" data-testid="offer-pdf-list">
                  {slots.map(({ slot, file, titolo }, idx) => {
                    const defaultTitle = slots.length > 1
                      ? (idx === 0 ? "La Tua Offerta" : `Proposta Alternativa ${idx + 1}`)
                      : "La Tua Offerta";
                    const cardTitle = titolo || defaultTitle;
                    const btnLabel = titolo
                      ? `Visualizza ${titolo}`
                      : (slots.length > 1
                          ? (idx === 0 ? "Visualizza Offerta Principale" : `Visualizza Proposta ${idx + 1}`)
                          : "Visualizza la tua Offerta");
                    return (
                      <Card key={slot} data-testid={`offer-pdf-card-${slot}`}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-600" />
                            {cardTitle}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
                            <FileText className="w-20 h-20 text-blue-400 mx-auto mb-4" />
                            <p className="text-slate-600 mb-6">
                              {slots.length > 1 && idx > 0
                                ? "Un'ulteriore proposta commerciale per la tua valutazione"
                                : "Clicca per visualizzare l'offerta completa"}
                            </p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                              <a
                                href={`${API}/files/${file}?v=${encodeURIComponent(offer.updated_at || offer.offerta_cloud_path || Date.now())}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button
                                  className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-6 py-5 h-auto"
                                  data-testid={slot === 1 ? "view-offer-btn" : `view-offer-btn-${slot}`}
                                >
                                  <Eye className="w-5 h-5 mr-2" />
                                  Visualizza
                                </Button>
                              </a>
                              <a
                                href={`${API}/files/${file}?download=true&v=${encodeURIComponent(offer.updated_at || offer.offerta_cloud_path || Date.now())}`}
                                download
                              >
                                <Button
                                  variant="outline"
                                  className="border-blue-400 text-blue-700 hover:bg-blue-100 text-lg px-6 py-5 h-auto"
                                  data-testid={slot === 1 ? "download-offer-btn" : `download-offer-btn-${slot}`}
                                >
                                  <Download className="w-5 h-5 mr-2" />
                                  Scarica
                                </Button>
                              </a>
                            </div>
                            {titolo && (
                              <p className="text-xs text-slate-500 italic mt-3">{btnLabel}</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                </>
              );
            })()}

            {/* Box Offerta Revisionata: visibile al cliente solo se PDF caricato, sempre visibile all'admin */}
            {(isAdmin || offer.offerta_revisionata_pdf) && (
              <Card data-testid="revisione-offerta-card" className={isAdmin && !offer.offerta_revisionata_pdf ? "border-dashed border-2 border-amber-300 bg-amber-50/40" : ""}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    Offerta Revisionata
                    {isAdmin && (
                      <span className="ml-2 text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded bg-amber-200 text-amber-900">
                        Solo Admin
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {offer.offerta_revisionata_pdf ? (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-8 text-center space-y-4">
                      <FileText className="w-20 h-20 text-indigo-400 mx-auto" />
                      <p className="text-slate-600">
                        Abbiamo caricato una versione revisionata della tua offerta.
                      </p>
                      <a
                        href={`${API}/files/${offer.offerta_revisionata_pdf}?v=${encodeURIComponent(offer.offerta_revisionata_uploaded_at || offer.offerta_revisionata_cloud_path || Date.now())}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          className="bg-indigo-600 hover:bg-indigo-700 text-white text-lg px-8 py-6 h-auto"
                          data-testid="view-revisione-btn"
                        >
                          <Download className="w-5 h-5 mr-3" />
                          Visualizza Offerta Revisionata
                        </Button>
                      </a>
                      {isAdmin && (
                        <div className="flex flex-wrap items-center justify-center gap-2 pt-4 border-t border-indigo-200">
                          <Button
                            variant="outline"
                            onClick={openRevisionePreview}
                            data-testid="notifica-revisione-btn"
                            className="border-indigo-400 text-indigo-800 hover:bg-indigo-100"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Notifica Cliente
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleDeleteRevisione}
                            data-testid="delete-revisione-btn"
                            className="border-rose-400 text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : isAdmin ? (
                    <FileDropZone
                      accept=".pdf,application/pdf"
                      disabled={uploadingRevisione}
                      onFile={(f) => handleUploadRevisione(f)}
                      dropLabel="Rilascia il PDF revisionato"
                      className="text-center space-y-3 py-6 rounded-md"
                    >
                      <p className="text-sm text-slate-600">
                        Nessuna revisione caricata. Trascina qui il PDF oppure clicca il pulsante.
                      </p>
                      <label className="inline-block">
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && handleUploadRevisione(e.target.files[0])}
                          data-testid="upload-revisione-input"
                        />
                        <Button
                          asChild
                          disabled={uploadingRevisione}
                          className="bg-amber-600 hover:bg-amber-700 text-white cursor-pointer"
                        >
                          <span>
                            {uploadingRevisione ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Caricamento...</>
                            ) : (
                              <><Upload className="w-4 h-4 mr-2" /> Carica PDF Offerta Revisionata</>
                            )}
                          </span>
                        </Button>
                      </label>
                    </FileDropZone>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {/* Countdown Modal */}
            {showCountdown && countdown !== null && (
              <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-300">
                <CardContent className="pt-6 text-center">
                  <Check className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    Grazie, la tua scelta è stata registrata
                  </h3>
                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <p className="text-lg text-slate-700 mb-3">
                      Verrai reindirizzato al modulo Google tra:
                    </p>
                    <div className="text-6xl font-bold text-blue-600">
                      {countdown}
                    </div>
                    <p className="text-sm text-slate-500 mt-3">secondi</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Response section */}
            {offer.offerta_pdf && !showCountdown && (
              <Card data-testid="response-card">
                <CardHeader>
                  <CardTitle>
                    {responded ? "La Tua Risposta" : "Rispondi all'Offerta"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {responded ? (
                    <div className={`p-6 rounded-lg text-center ${
                      offer.stato === "accettato" ? "bg-emerald-50" : "bg-rose-50"
                    }`}>
                      {offer.stato === "accettato" ? (
                        <>
                          <Check className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                          <h3 className="text-xl font-bold mb-2 text-emerald-800">
                            Complimenti: Offerta Accettata!
                          </h3>
                          <p className="text-sm text-slate-600 mb-4">
                            Se il Browser non ti ha mostrato il Modulo Google per la comunicazione della scelta allo Showroom, qui sotto trovi il Link Statico
                          </p>
                          <a
                            href="https://forms.gle/guxUoHmUHfB5Je6t6"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                              Comunica la scelta
                            </Button>
                          </a>
                        </>
                      ) : (
                        <>
                          <X className="w-12 h-12 text-rose-600 mx-auto mb-3" />
                          <h3 className="text-xl font-bold mb-2 text-rose-800">
                            Offerta {offer.stato}
                          </h3>
                        </>
                      )}
                      {offer.note_cliente && (
                        <p className="text-slate-600 mt-3">&quot;{offer.note_cliente}&quot;</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Note o Commenti (opzionale)</Label>
                        <Textarea
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Inserisci eventuali note o commenti..."
                          rows={4}
                          data-testid="response-note"
                        />
                      </div>
                      <div className="flex gap-4">
                        <Button
                          onClick={() => handleResponse(true)}
                          disabled={submitting}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          data-testid="accept-btn"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Accetta Offerta
                        </Button>
                        <Button
                          onClick={() => handleResponse(false)}
                          disabled={submitting}
                          variant="outline"
                          className="flex-1 border-rose-200 text-rose-600 hover:bg-rose-50"
                          data-testid="reject-btn"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Rifiuta
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* I Prossimi Passi - Solo se offerta accettata */}
            {responded && offer.stato === "accettato" && data?.settings?.video_prossimi_passi && (
              <Card data-testid="prossimi-passi-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    I Prossimi Passi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-slate-100">
                    <iframe
                      width="100%"
                      height="100%"
                      src={data.settings.video_prossimi_passi.replace('watch?v=', 'embed/')}
                      title="I Prossimi Passi"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="md:col-span-4 space-y-6">
            {/* Il Prodotto (ex Cataloghi) */}
            {cataloghiDocs.length > 0 && (
              <Card data-testid="cataloghi-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    Il Prodotto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {cataloghiDocs.map((doc) => (
                      <a
                        key={doc.id}
                        href={`${API}/files/${doc.file_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 text-sm text-slate-700 hover:text-slate-900"
                      >
                        <FileText className="w-4 h-4 text-slate-400" />
                        {doc.nome}
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Documento Marketing Personalizzato */}
            {hasPersonalizedDoc && (
              <Card data-testid="doc-personalizzato-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    Il Prodotto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a
                    href={`${API}/files/${offer.doc_marketing_personalizzato}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 rounded bg-slate-50 hover:bg-slate-100 text-sm text-slate-700 hover:text-slate-900"
                  >
                    <FileText className="w-5 h-5 text-blue-500" />
                    Visualizza Documento
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Schede */}
            {schedeDocs.length > 0 && (
              <Card data-testid="schede-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    Schede Tecniche
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {schedeDocs.map((doc) => (
                      <a
                        key={doc.id}
                        href={`${API}/files/${doc.file_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 text-sm text-slate-700 hover:text-slate-900"
                      >
                        <FileText className="w-4 h-4 text-slate-400" />
                        {doc.nome}
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Le Garanzie (ex Altri documenti) */}
            {offer.altri_documenti && offer.altri_documenti.length > 0 && (
              <Card data-testid="altri-doc-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Le Garanzie
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {offer.altri_documenti.map((doc, idx) => (
                      <a
                        key={doc || `altrodoc-${idx}`}
                        href={`${API}/files/${doc}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 rounded bg-slate-50 hover:bg-slate-100 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <FileText className="w-4 h-4" />
                        Visualizza Documento
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Prendi Appuntamento */}
            <Card className="bg-emerald-600 text-white">
              <CardContent className="pt-6">
                <a 
                  href="https://calendly.com/showroomingenio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:opacity-90 transition-opacity"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Prendi Appuntamento</h3>
                    <p className="text-sm text-emerald-100">Clicca per prenotare</p>
                  </div>
                </a>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="text-white bg-[#1e40af]">
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-3 text-white">Hai domande?</h3>
                <p className="text-sm text-blue-100 mb-4">
                  Contattaci per qualsiasi chiarimento sulla tua offerta. Ecco i nostri contatti:
                </p>
                <div className="text-sm space-y-2">
                  <p className="text-blue-100">
                    <span className="font-medium text-white">Fisso:</span> 070243952
                  </p>
                  <p className="text-blue-100">
                    <span className="font-medium text-white">Mail:</span> info@showroomingenio.it
                  </p>
                  <p className="text-blue-100">
                    <span className="font-medium text-white">Whatsapp:</span> 350521782
                  </p>
                  <p className="text-blue-100">
                    <span className="font-medium text-white">Sede:</span> Via Puccini 71 Angolo via Tuveri
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Social */}
            <Card className="bg-[#1e40af] text-white">
              <CardContent className="pt-6">
                <p className="text-sm font-medium mb-4 text-white">
                  Cercaci e Seguici sui nostri Canali Social
                </p>
                <div className="flex items-center gap-4">
                  <a 
                    href="https://www.youtube.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <Youtube className="w-6 h-6 text-white" />
                  </a>
                  <a 
                    href="https://www.instagram.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <Instagram className="w-6 h-6 text-white" />
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Serve altro? */}
            <Card className="bg-white">
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4 text-slate-800">Serve altro?</h3>
                <img 
                  src="https://customer-assets.emergentagent.com/job_111140fb-8270-46a4-8f14-2e28f9dc367f/artifacts/feb06o26_Infissi%20cagliari%20testimonial%20Ingenio.png"
                  alt="Showroom Ingenio"
                  className="w-full rounded-lg mb-4"
                />
                <p className="text-sm text-slate-600">
                  Ricorda di visitare{" "}
                  <a 
                    href="https://www.showroomingenio.it" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    www.showroomingenio.it
                  </a>
                  {" "}per scoprire tutta l'Offerta!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
        )}

        {/* Tab Content: Contratto e Fatture */}
        {activeTab === "contratto-fatture" && (
          <div className="w-full space-y-6" data-testid="tab-contratto-fatture-content">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <p className="text-sm text-teal-900">
                📁 In questa sezione trovi <strong>tutti i documenti</strong> del tuo contratto: quelli che hai firmato, i documenti personali caricati, le fatture emesse e le certificazioni/garanzie.
                <br />Puoi scaricarli in qualsiasi momento.
              </p>
            </div>

            {/* Sezione 1: Contratti Firmati (documenti_firmati) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700 text-base">
                  ✍️ Contratti Firmati
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(offer.documenti_firmati || []).length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Nessun contratto firmato ancora presente.</p>
                ) : (
                  <ul className="space-y-2">
                    {(offer.documenti_firmati || []).map((doc, i) => (
                      <li key={doc.file_path || `firm-${i}`} className="flex items-center justify-between gap-2 p-2 bg-amber-50 border border-amber-200 rounded">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-amber-600 shrink-0" />
                          <span className="text-sm text-slate-800 truncate">{doc.filename || doc.file_path}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <a href={`${API}/files/${doc.file_path}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="text-xs h-7 border-blue-400 text-blue-700 hover:bg-blue-50"><Eye className="w-3 h-3 mr-1" />Vedi</Button>
                          </a>
                          <a href={`${API}/files/${doc.file_path}?download=true`} download>
                            <Button size="sm" variant="outline" className="text-xs h-7"><Download className="w-3 h-3 mr-1" />Scarica</Button>
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Sezione 2: Documenti Personali caricati */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-700 text-base">
                  🪪 Documenti Personali Caricati
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(offer.documenti_personali_caricati || []).length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Nessun documento personale caricato.</p>
                ) : (
                  <ul className="space-y-2">
                    {(offer.documenti_personali_caricati || []).map((doc, i) => (
                      <li key={doc.slot || `dp-${i}`} className="flex items-center justify-between gap-2 p-2 bg-purple-50 border border-purple-200 rounded">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-purple-600 shrink-0" />
                          <span className="text-sm font-medium text-slate-800 shrink-0">{doc.label}</span>
                          <span className="text-xs text-slate-500 italic truncate">{doc.filename}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <a href={`${API}/client/offer/${magicLink}/documenti-personali/${doc.slot}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="text-xs h-7 border-blue-400 text-blue-700 hover:bg-blue-50"><Eye className="w-3 h-3 mr-1" />Vedi</Button>
                          </a>
                          <a href={`${API}/client/offer/${magicLink}/documenti-personali/${doc.slot}?download=true`} download>
                            <Button size="sm" variant="outline" className="text-xs h-7"><Download className="w-3 h-3 mr-1" />Scarica</Button>
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Sezione 3: Fatture (contabili tranche pagamento) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700 text-base">
                  💰 Fatture
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(data?.payments_fatture || []).length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Nessuna fattura disponibile.</p>
                ) : (
                  <ul className="space-y-2">
                    {(data?.payments_fatture || []).map((f, i) => (
                      <li key={`${f.payment_id}-${f.tranche_key}`} className="flex items-center justify-between gap-2 p-2 bg-blue-50 border border-blue-200 rounded">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                          <span className="text-sm font-medium text-slate-800 shrink-0">{f.tranche_label}</span>
                          <span className="text-xs text-slate-500 italic truncate">{f.filename}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <a href={`${API}${f.download_url}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="text-xs h-7 border-blue-400 text-blue-700 hover:bg-blue-50"><Eye className="w-3 h-3 mr-1" />Vedi</Button>
                          </a>
                          <a href={`${API}${f.download_url}?download=true`} download>
                            <Button size="sm" variant="outline" className="text-xs h-7"><Download className="w-3 h-3 mr-1" />Scarica</Button>
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Sezione 4: Certificazioni, Garanzia e altri documenti */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700 text-base">
                  🏆 Certificazioni e Garanzia
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(offer.documenti_extra || []).length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Nessun documento extra disponibile.</p>
                ) : (
                  <ul className="space-y-2">
                    {(offer.documenti_extra || []).map((doc) => (
                      <li key={doc.id} className="flex items-center justify-between gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold shrink-0 uppercase">{doc.tipo}</span>
                          <span className="text-sm text-slate-800 truncate">{doc.label}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <a href={`${API}/client/offer/${magicLink}/documento-extra/${doc.id}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="text-xs h-7 border-blue-400 text-blue-700 hover:bg-blue-50"><Eye className="w-3 h-3 mr-1" />Vedi</Button>
                          </a>
                          <a href={`${API}/client/offer/${magicLink}/documento-extra/${doc.id}?download=true`} download>
                            <Button size="sm" variant="outline" className="text-xs h-7"><Download className="w-3 h-3 mr-1" />Scarica</Button>
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Content: Fatture e Collaudo */}
        {activeTab === "fatture-collaudo" && (
          <div className="space-y-6">
            {/* Sezione Fatture */}
            {offer.fatture && offer.fatture.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    💰 Fatture
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {offer.fatture.map((doc, index) => (
                      <div key={doc.file_path || `fattura-${index}`} className="flex items-center justify-between bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-slate-800">{doc.original_filename}</p>
                            <p className="text-sm text-slate-500">
                              Caricato il {new Date(doc.data_caricamento).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`${API}/files/${doc.file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 border border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Visualizza
                          </a>
                          <a
                            href={`${API}/files/${doc.file_path}?download=true`}
                            download
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            Scarica
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sezione Collaudo */}
            {offer.collaudo && offer.collaudo.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-emerald-700">
                    ✅ Documenti di Collaudo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {offer.collaudo.map((doc, index) => (
                      <div key={doc.file_path || `collaudo-${index}`} className="flex items-center justify-between bg-emerald-50 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-emerald-600" />
                          <div>
                            <p className="font-medium text-slate-800">{doc.original_filename}</p>
                            <p className="text-sm text-slate-500">
                              Caricato il {new Date(doc.data_caricamento).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`${API}/files/${doc.file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-400 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Visualizza
                          </a>
                          <a
                            href={`${API}/files/${doc.file_path}?download=true`}
                            download
                            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            Scarica
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Dialog Preview Email — Notifica Revisione Offerta (solo admin) */}
      <Dialog open={revisionePreviewOpen} onOpenChange={(v) => { if (!revisionePreview.sending && !v) setRevisionePreviewOpen(false); }}>
        <DialogContent
          data-testid="revisione-preview-modal"
          className="max-w-5xl w-[95vw] h-[92vh] p-0 flex flex-col overflow-hidden"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b bg-slate-50 shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Eye className="w-5 h-5 text-indigo-600" />
              Anteprima Email — Revisione della tua Offerta
            </DialogTitle>
            <div className="text-sm text-slate-600 pt-2 space-y-0.5">
              <div><span className="font-medium">A:</span> {revisionePreview.to || "—"}</div>
              <div><span className="font-medium">BCC:</span> {revisionePreview.bcc || "—"}</div>
              <div><span className="font-medium">Oggetto:</span> {revisionePreview.subject || "—"}</div>
            </div>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-hidden bg-slate-100">
            {revisionePreview.loading ? (
              <div className="flex items-center justify-center h-full text-slate-500">
                <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                Caricamento anteprima...
              </div>
            ) : revisionePreview.editMode ? (
              <div className="h-full overflow-auto p-6 space-y-4 bg-white">
                <div>
                  <Label htmlFor="rev-subj">Oggetto</Label>
                  <Input
                    id="rev-subj"
                    data-testid="revisione-preview-edit-subject"
                    value={revisionePreview.subject}
                    onChange={(e) => setRevisionePreview((s) => ({ ...s, subject: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="rev-html">Corpo HTML</Label>
                  <Textarea
                    id="rev-html"
                    data-testid="revisione-preview-edit-html"
                    value={revisionePreview.html}
                    onChange={(e) => setRevisionePreview((s) => ({ ...s, html: e.target.value }))}
                    rows={20}
                    className="mt-1 font-mono text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="rev-text">Fallback plain-text</Label>
                  <Textarea
                    id="rev-text"
                    data-testid="revisione-preview-edit-text"
                    value={revisionePreview.text}
                    onChange={(e) => setRevisionePreview((s) => ({ ...s, text: e.target.value }))}
                    rows={8}
                    className="mt-1 font-mono text-xs"
                  />
                </div>
              </div>
            ) : (
              <iframe
                title="revisione-preview"
                data-testid="revisione-preview-iframe"
                srcDoc={revisionePreview.html}
                sandbox=""
                className="w-full h-full border-0 bg-white"
              />
            )}
          </div>

          <div className="border-t bg-white px-6 py-3 flex flex-wrap items-center justify-between gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRevisionePreview((s) => ({ ...s, editMode: !s.editMode }))}
              disabled={revisionePreview.loading || revisionePreview.sending}
              data-testid="revisione-preview-toggle-edit"
            >
              {revisionePreview.editMode ? (
                <><Eye className="w-4 h-4 mr-2" /> Torna all&apos;anteprima</>
              ) : (
                <><Edit className="w-4 h-4 mr-2" /> Modifica testo</>
              )}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRevisionePreviewOpen(false)}
                disabled={revisionePreview.sending}
                data-testid="revisione-preview-cancel"
              >
                Annulla
              </Button>
              <Button
                type="button"
                onClick={confirmSendRevisione}
                disabled={revisionePreview.loading || revisionePreview.sending || !revisionePreview.html}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="revisione-preview-confirm-send"
              >
                {revisionePreview.sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Conferma e invia</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
